import { CurrencyPipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { IColumnDetails, ITableData } from '../../models/ITableData';
import { IMenuOptions } from '../../models/IMenuOptions';
import { MatSort } from '@angular/material/sort';
import { SingleImageLoaderController } from '../../services/single-image-loader-controller/single-image-loader-controller.service';
import { FileUpload } from '../../models/IFileUpload';
import { IOption } from '../../models/IOption';
import { CSVService } from '../../services/csv-menu/csv.service';
import { IFileData } from '../../models/IFileData';

@Component({
  selector: 'ff-input-generic-table',
  templateUrl: './input-generic-table.component.html',
  styleUrls: ['./input-generic-table.component.scss'],
})
export class InputGenericTableComponent implements OnInit {
  constructor(
    private currencyPipe: CurrencyPipe,
    private singleImageLoaderController: SingleImageLoaderController
  ) {}

  @ViewChild(MatTable) table: MatTable<ITableData>;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  /**
   * The title name to show at the top of the table
   * @internal
   */
   @Input() titleName: string = 'Category';

  /**
   * The title to show at the top of the table
   * @internal
   */
  @Input() title: string = '';

  /**
   * The attributes belonging to each item in the table
   * @internal
   */

  private _inputProperties: IColumnDetails[] = [];

  @Input()
  set inputProperties(value : IColumnDetails[]){
    this._inputProperties = value;
  }

  get inputProperties(){
    return this._inputProperties;
  }

  @Input() hasImage: boolean = false;
  /**
   * Options to display as menu items in the table
   * Use the IMenuOptions interface: { label: string; icon?: string; callback: () => void; args?: any[]; }
   * * @internal
   */
  @Input() upperList: IMenuOptions[];

  @Input() showQuantity: boolean = false;

  /**
   * Return the options updated each time the user check a radio button
   * @internal
   */
  @Output() data = new EventEmitter<ITableData[]>();

  //Base model with the abstract properties from the items in the table
  baseModel: ITableData = {};

  //String list to use as header in each column
  columnList: string[] = [];

  //Properties list
  propList: string[] = [];
  modelList: ITableData[] = [];
  dataSource = new MatTableDataSource<ITableData>(this.modelList);
  multipleRows: boolean = false;
  tableRows: number = 1;
  imageElement: ITableData;
  missingInfo: boolean = false;

  //error counter
  private errorCounter: any[] = [];

  imageLoaderModal = async (event: Event) => {
    this.singleImageLoaderController.createSingleModal('Add Photo', {
      onSave: this.onSave,
    });
    await this.singleImageLoaderController.present().then((value) => {
      this.updateModel();
    });
  };

  ngOnInit() {
       //adds items's properties to the base model
    this.inputProperties.forEach(prop => {
      this.baseModel[prop.columnName] = '';
    });
    this.propList = this.inputProperties.map((prop) => prop.columnName);

    //Creates new obj to create first row
    const obj = this.createRow();
    this.modelList.push(obj);

    //Updates table datasource and menu args
    this.dataSource.data = [...this.modelList];
    this.upperList[0].args = this.modelList;
    this.upperList[1].args = this.modelList;
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  ngDoCheck() {
    this.data.emit(this.modelList);
  }

  onSelectionChange = (): void => {
    this.data.emit(this.modelList);
  };

  //Format data type currency
  transformAmount(element) {
    let value = element.value;

    if (!isNaN(value)) {
      value = this.currencyPipe.transform(value, '$');
    } else {
      if (!isNaN(value.substring(1))) {
        value = this.currencyPipe.transform(value.substring(1), '$');
      }
    }
    element.value = value;
  }

  //Converts input in case that it has '$' or ',' to be formatted and read by the model
  onFocusCost(event) {
    let val = event.target.value as string;

    val = val.split(',').join('');

    if (event.target.value.charAt(0) == '$') {
      val = val.substr(1);
    }

    event.target.value = val;
  }

  //Format data type months
  transformMonth(element) {
    let value = element.value;

    if (!isNaN(value) && value !== '') {
      value = value + ' mo';
    }
    element.value = value;
  }

    //Format selected date
    dateSelection(element){
      return element ? new Date(element) : undefined;
    }

  //Removes 'mo' format from the input at the moment of focusing on the input
  onFocusMonth(event){
    let val = event.target.value as string;

    val = val.slice(0, -3);

    event.target.value = val;
  }

  //Format data type days
  transformDays(element) {
    let value = element.value;

    if (!isNaN(value) && value !== '') {
      value = value + ' d';
    }
    element.value = value;
  }

  //Creates a new object to insert in the list using the base model
  createRow(data?: ITableData) {
    const newData = Object.assign({}, this.baseModel);

    if (data !== undefined) {
      const props = Object.keys(newData);
      props.forEach((prop) => {
        if (prop !== 'file') newData[prop] = data[prop];
      });
    }

    return newData;
  }

  deleteRow(row){
    let index = this.modelList.findIndex(x => x === row);
    this.modelList.splice(index, 1);
    this.updateModel();
    this.data.emit(this.modelList);
  }

  //Listens to any change in the row quantity input and adjust the model
  rowChange(event) {
    if (event === null) {
      event = this.modelList.length;
    }

    const modelListLength = this.modelList.length;
    const diff = Math.abs(modelListLength - event);

    if (modelListLength > event) {
      this.modelList.splice(modelListLength - diff);
    } else {
      for (let i = 0; i < diff; i++) {
        //Creates new obj to create new rows
        let obj = null;
        obj = this.createRow();
        this.modelList.push(obj);
      }
    }

    this.multipleRows = this.modelList.length > 1 ? true : false;

    this.updateModel();
  }

  //Adds new item to the prop list to create new column with the three dot menu
  rowColumns(list: string[]) {
    let newList: string[] = [...list];
    // newList.push('documentMenu');
    return newList;
  }

  onSave = async (file: FileUpload) => {
    if (file.applyToAll) {
      this.modelList.forEach((elem) => {
        elem.photo = file.file;
      });
    } else {
      this.imageElement.photo = file.file;
    }
  };

  displayModal = (event: Event, element: any): void => {
    this.imageElement = element;

    this.imageLoaderModal(event);
  };

  updateModel(): void {
    this.dataSource.data = [...this.modelList];
    this.upperList[0].args = this.modelList;
    this.upperList[1].args = this.modelList;
    this.table.renderRows();
  }

  focusOutFunction(event, isRequired) {
    const element = event.target as HTMLInputElement;
    if(!isRequired) return;

    this.errorCounter = this.errorCounter.filter(x => x != element);

    if (!element.value) {
      element.style.borderColor = 'red';
      this.errorCounter.push(element);
    } else {
      element.style.borderColor = 'rgba(164, 164, 164, 0.4)';
    }
    this.missingInfo = this.errorCounter.length > 0;
  }

  handleCSVData(event: IFileData) {
    const rows = CSVService.transformData(event.data);
    this.createRows(rows);
  }

  createRows(rows: ITableData[]) {
    this.modelList = [];

    rows.forEach((elem: ITableData) => {
      //Creates new obj to create new rows
      let obj = null;
      obj = this.createRow(elem);

      this.modelList.push(obj);
    });

    this.updateModel();
    this.tableRows = rows.length;
  }

  //Filter for decimals
  keyPressDecimal(event: KeyboardEvent) {
    let value = (event.target as HTMLInputElement).value;

    let key = event.key;

    if (value.includes('.') || value == '') {
      return key in '0123456789'.split('');
    } else {
      return key in '0123456789'.split('') || key == '.';
    }
  }

  //Filter for integers
  keyPressInt(event: KeyboardEvent) {
    let key = event.key;
    return key in '0123456789'.split('');
  }

  isRequired(required: boolean) {
    return required
      ? 'table-input table-input-text-large table-input-required'
      : 'table-input table-input-text-large';
  }

  setOptionDisabled(option: IOption, data: any, hasKey: boolean){
    //if row has id/key disable all options
    if(hasKey) return true;
    //if the option value is part of the selected serial numbers for the current control, then disable setting will be ignored
    if(data?.includes(option.value)) return false;
    //otherwise disable setting will be returned
    else return option.disabled;
  }
}
